import { DealStep, NavOption, StepDetail } from "../types";

export const NDA_DESCRIPTION = `
  This MNDA protects you and any information shared between you and ESO Fund. It also prohibits the sharing of ESO Fund materials with anyone other than the professional advisors assisting you on this transaction.
  <br /><br />
  After signing, please send me the following items, ensuring that both the <strong>company name</strong> and <strong>current date</strong> are visible:
  <br /><br />
  1) <strong>Proof of ownership</strong>:
  <br />
    - If you have options, please share screenshots or grant documents indicating the <strong>vested amounts, grant dates, and strike prices</strong><br />
    - If you have shares, please share a screenshot of your stock certificates
  <br /><br />
  2) <strong>The current FMV (Fair Market Value), aka 409A share price</strong>:
  <br />
    - In Carta, the FMV may be found on either the Portfolio page, or near the bottom Exercise Simulator page<br />
    - In Shareworks, the FMV may be found on the Dashboard<br />
    - Other equity portals will typically display the FMV on a summary page<br />
    - Otherwise, ask your stock plan administrator or CFO for the current 409A value
  <br /><br />
  Once we receive these documents, we will do our best to get you an offer out as soon as possible.
  <br /><br />
  For an overview of our funding process, please go to our <a href='https://esofund.com/overview'>website</a>.
    `

export const NAV_OPTIONS = [
  NavOption.DOCUMENTS,
  NavOption.COMPLIANCE,
  NavOption.CURRENT_OFFER,
]

export const NAV_OPTIONS_NO_DEALS = [
  NavOption.DOCUMENTS,
]

export const DEAL_MANAGER_NAV_OPTIONS = [
  NavOption.PIPELINE,
  NavOption.HOTLIST,
  NavOption.FMV_REVIEW,
  NavOption.IRR,
  NavOption.EMPLOYEE_OUTREACH,
  NavOption.PORTAL_EXTRACTION,
  NavOption.STRANDED_VALUE,
  NavOption.ACTIVITY,
  NavOption.REPORTS,
  NavOption.SETTINGS,
]
  

export const STATE_ABBREVIATIONS_MAP = {
  "Alabama": "AL",
  "Alaska": "AK",
  "Arizona": "AZ",
  "Arkansas": "AR",
  "California": "CA",
  "Colorado": "CO",
  "Connecticut": "CT",
  "Delaware": "DE",
  "Florida": "FL",
  "Georgia": "GA",
  "Hawaii": "HI",
  "Idaho": "ID",
  "Illinois": "IL",
  "Indiana": "IN",
  "Iowa": "IA",
  "Kansas": "KS",
  "Kentucky": "KY",
  "Louisiana": "LA",
  "Maine": "ME",
  "Maryland": "MD",
  "Massachusetts": "MA",
  "Michigan": "MI",
  "Minnesota": "MN",
  "Mississippi": "MS",
  "Missouri": "MO",
  "Montana": "MT",
  "Nebraska": "NE",
  "Nevada": "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  "Ohio": "OH",
  "Oklahoma": "OK",
  "Oregon": "OR",
  "Pennsylvania": "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  "Tennessee": "TN",
  "Texas": "TX",
  "Utah": "UT",
  "Vermont": "VT",
  "Virginia": "VA",
  "Washington": "WA",
  "West Virginia": "WV",
  "Wisconsin": "WI",
  "Wyoming": "WY"
}

export const STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming"
]

const ndaDescription = `Navigate to the "Compliance" section and select the NDA tab. Fill out the form and submit it.`
const signNdaDescription = `Once you have submitted the NDA form, you will receive an email from DocuSign to electronically sign the NDA. Please check your spam folder if you do not see it in your inbox.` 
const uploadGrantDocumentsDescription = `After signing the NDA, you can upload your grant documents in the "Documents" section. Please ensure that the documents clearly show your vested amounts, grant dates, and strike prices.`
const acceptOfferDescription = `Once your offer is ready, you will receive an email notification. You can then review and accept the offer in the "Your Deals" section.`
const completeBackgroundCheckFormDescription = `Navigate to the "Compliance" section and select the Background Check tab. Fill out the form and submit it.`
const signBackgroundCheckDescription = `After submitting the Background Check form, you will receive an email from DocuSign to electronically sign the Background Check authorization. Please check your spam folder if you do not see it in your inbox.`
const scheduleClosingMeetingDescription = `Once you have signed the Background Check authorization, you will receive an email with a link to schedule your closing meeting. Please choose a time that works best for you.`
const signContractDescription = `After scheduling the closing meeting, you will receive an email from DocuSign to electronically sign the contract. Please check your spam folder if you do not see it in your inbox.`
const sendWireInformationDescription = `Once you have signed the contract, please provide your wire information to facilitate the transfer of funds. You can send this information via email or through the secure portal.`

export const COMPLETE_NDA_FORM_STEP_INDEX = 0;
export const SIGN_NDA_STEP_INDEX = 1;
export const BC_FORM_STEP_INDEX = 4;
export const SIGN_BC_STEP_INDEX = 5;
export const SCHEDULE_CLOSING_MEETING_STEP_INDEX = 6;
export const SIGN_CONTRACT_STEP_INDEX = 7;

export const ALL_STEPS: StepDetail[] = [
  { step: DealStep.COMPLETE_NDA_FORM, title: "Complete NDA Form", description: ndaDescription, completed: false, isActive: false },
  { step: DealStep.SIGN_NDA, title: "Sign NDA", description: signNdaDescription, completed: false, isActive: false },
  { step: DealStep.UPLOAD_GRANT_DOCS, title: "Upload Grant Documents", description: uploadGrantDocumentsDescription, completed: false, isActive: false },
  { step: DealStep.ACCEPT_OFFER, title: "Accept Offer", description: acceptOfferDescription, completed: false, isActive: false },
  { step: DealStep.COMPLETE_BC_FORM, title: "Complete Background Check Form", description: completeBackgroundCheckFormDescription, completed: false, isActive: false },
  { step: DealStep.SIGN_BC, title: "Sign Background Check", description: signBackgroundCheckDescription, completed: false, isActive: false },
  { step: DealStep.SCHEDULE_CLOSING_MEETING, title: "Schedule Closing Meeting", description: scheduleClosingMeetingDescription, completed: false, isActive: false },
  { step: DealStep.SIGN_CONTRACT, title: "Sign Contract", description: signContractDescription, completed: false, isActive: false },
  { step: DealStep.SEND_WIRE_INFO, title: "Send Wire Information", description: sendWireInformationDescription, completed: false, isActive: false }
]

export const BACKGROUND_CHECK_STEPS = [
  { title: "Upload Grant Documents", description: uploadGrantDocumentsDescription, completed: true, isActive: false },
  { title: "Accept Offer", description: acceptOfferDescription, completed: true, isActive: false },
  { title: "Complete Background Check Form", description: completeBackgroundCheckFormDescription, completed: false, isActive: true }
]